<template>
  <div class="cell" :class="cellClass" @click="onClick">
    <div class="toprow">
      <span class="dn tnum">{{ dayNum }}</span>
      <span v-if="showDot" class="sdot"></span>
    </div>
    <span v-if="chip" class="chiptag">{{ chip }}</span>
  </div>
</template>

<script>
import moment from "moment";
import { slotMinutes, formatHoursShort } from "@/utils/workingHoursFormat";

export default {
  name: "CalendarCell",
  props: {
    date: { type: String, required: true }, // YYYY-MM-DD
    isOutsideMonth: { type: Boolean, default: false },
    isSelected: { type: Boolean, default: false },
    slots: { type: Array, default: () => [] },
    overrideMarkers: { type: Array, default: () => [] },
  },
  methods: {
    onClick(e) {
      // Pass modifier intent up so the grid can do single / toggle / range.
      this.$emit("select", {
        date: this.date,
        additive: e.metaKey || e.ctrlKey,
        range: e.shiftKey,
      });
    },
  },
  computed: {
    dayNum() {
      return moment(this.date).date();
    },
    isWeekend() {
      const dow = moment(this.date).day(); // 0=Sun, 6=Sat
      return dow === 0 || dow === 6;
    },
    isToday() {
      return moment(this.date).isSame(moment(), "day");
    },
    minutes() {
      return slotMinutes(this.slots);
    },
    // One canonical status drives both the cell tint and the dot/chip.
    status() {
      if (this.isOutsideMonth) return "pad";
      if (this.overrideMarkers.includes("leave-whole")) return "leave";
      if (this.overrideMarkers.includes("extra-shift")) return "extra";
      if (this.overrideMarkers.includes("leave-partial")) return "partial";
      if (this.slots.length > 0) return "work";
      return "off";
    },
    chip() {
      if (this.status === "leave") return "ลา";
      if (this.status === "off" || this.status === "pad") return null;
      if (this.minutes <= 0) return null;
      return formatHoursShort(this.minutes);
    },
    showDot() {
      return this.status !== "off" && this.status !== "pad";
    },
    cellClass() {
      return {
        muted: this.isOutsideMonth,
        weekend: !this.isOutsideMonth && this.isWeekend,
        ["s-" + this.status]: this.status !== "pad",
        sel: this.isSelected,
        today: this.isToday && !this.isOutsideMonth,
      };
    },
  },
};
</script>

<style scoped>
.cell {
  position: relative;
  border: 1px solid var(--line);
  background: var(--surface);
  border-radius: var(--r);
  padding: 6px 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 0;
  user-select: none;
  overflow: hidden;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s;
}
.cell:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-card);
  border-color: var(--line-strong);
  z-index: 1;
}
.cell.muted {
  background: var(--surface-2);
  border-color: transparent;
  cursor: default;
}
.cell.muted:hover {
  transform: none;
  box-shadow: none;
  border-color: transparent;
}
.cell.muted .dn {
  color: var(--faint);
  font-weight: 400;
}
.cell.weekend {
  background: var(--surface-2);
}
.cell.weekend .dn {
  color: var(--muted);
}

/* status tints */
.cell.s-work {
  background: var(--work-t);
  border-color: var(--work-b);
}
.cell.s-extra {
  background: var(--extra-t);
  border-color: var(--extra-b);
}
.cell.s-leave {
  background: var(--leave-t);
  border-color: var(--leave-b);
}
.cell.s-partial {
  background: var(--partial-t);
  border-color: var(--partial-b);
}
.cell.s-off {
  background: var(--off-t);
  border-color: var(--off-b);
}

.toprow {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.dn {
  font-family: var(--wh-num);
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-2);
  line-height: 1;
}
.sdot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
}
.s-work .sdot {
  background: var(--work);
}
.s-extra .sdot {
  background: var(--extra);
}
.s-leave .sdot {
  background: var(--leave);
}
.s-partial .sdot {
  background: var(--partial);
}

.chiptag {
  margin-top: auto;
  align-self: flex-start;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  padding: 2px 8px;
  border-radius: var(--r-pill);
  background: hsl(0 0% 100% / 0.72);
  max-width: 100%;
}
.s-work .chiptag {
  color: var(--work);
}
.s-extra .chiptag {
  color: var(--extra);
}
.s-leave .chiptag {
  color: var(--leave);
}
.s-partial .chiptag {
  color: var(--partial);
}
.s-off .chiptag {
  color: var(--off);
}

/* today */
.cell.today .dn {
  color: #fff;
  background: var(--brand);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: grid;
  place-items: center;
}
/* selected */
.cell.sel {
  box-shadow: 0 0 0 2px var(--surface), 0 0 0 4px var(--brand);
  border-color: transparent;
  z-index: 2;
}
.cell.sel:hover {
  transform: none;
}
</style>
